/**
 * Shown wherever MCP is reached on an account that doesn't have it (ENG-74).
 *
 * Two audiences, two wordings. In Settings the reader is an admin of the
 * account who can act on "talk to your account rep". On the consent screen the
 * reader arrived from Claude mid-authorization, may not administer anything,
 * and mainly needs to know the attempt has stopped and why — so that copy also
 * says what happens next.
 *
 * Neither mentions LaunchDarkly, flags, or MCP as an acronym without expanding
 * it. "Not enabled on your account" is the whole of what the reader can act on.
 */
import { Lock } from 'lucide-react';

type Props = {
  /** Where this is rendered — picks the wording, not the styling. */
  context: 'settings' | 'authorize';
  /** Account name, when we know it. The consent screen usually does. */
  accountName?: string;
};

export default function McpDisabledNotice({ context, accountName }: Props) {
  const who = accountName ? <span className="font-medium">{accountName}</span> : 'your account';

  return (
    <div className="flex min-h-[320px] items-center justify-center p-6">
      <div className="max-w-md space-y-3 text-center">
        <Lock className="mx-auto h-6 w-6 text-muted-foreground" aria-hidden />
        <h2 className="text-base font-semibold">
          {context === 'settings'
            ? 'Connectors are not enabled'
            : 'This account cannot be connected'}
        </h2>
        {context === 'settings' ? (
          <p className="text-sm text-muted-foreground">
            Connecting AI assistants to {who} isn't enabled yet. Reach out to your
            account representative to turn it on.
          </p>
        ) : (
          <p className="text-sm text-muted-foreground">
            Connecting AI assistants to {who} isn't enabled, so this connection can't be
            completed. Reach out to your account representative to turn it on, then try
            again.
          </p>
        )}
      </div>
    </div>
  );
}
